import { OrderStatus } from "@/types/tms";
import type { ConfirmDocsOverrideAck } from "@/lib/confirm-docs-override-event";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { PLANNING_TZ } from "@/lib/timezone";

const todayDate = () => format(new Date(), "yyyy-MM-dd");
/** Default window length (minutes) when seeding a fresh form. Also drives
 *  the "ceil-to-next-slot" rounding in `seedFutureWindow`, which enforces
 *  the minimum lead time between "now" and a new order's ready time.
 *
 *  Live-binding module variable: the value is admin-adjustable via the
 *  `order_window_minutes` row in `public.app_settings`. `setDefaultWindowMinutes`
 *  is called once at app boot (see `OrderWindowMinutesSync` in `App.tsx`)
 *  and again whenever an admin edits the value. All consumers import the
 *  `let` binding directly and observe the update on next render.
 *
 *  Fallback: 60 minutes (preserves legacy behavior when the setting row is
 *  missing or the network fetch is in flight). */
export let DEFAULT_WINDOW_MINUTES = 60;

/** Update the module-level default. Clamped to [5, 480] minutes. Callers
 *  should invalidate the `useOrderWindowMinutes` query after persisting to
 *  the DB so this setter fires on the next tick. */
export function setDefaultWindowMinutes(n: number): void {
  if (!Number.isFinite(n)) return;
  const clamped = Math.max(5, Math.min(480, Math.round(n)));
  DEFAULT_WINDOW_MINUTES = clamped;
}

/** Add `minutes` to a `YYYY-MM-DDTHH:mm` local-naive datetime string.
 *  Returned in the same local-naive format. */
export function addMinutesLocal(local: string, minutes: number): string {
  if (!local || !local.includes("T")) return local;
  const [date, time] = local.split("T");
  const [hh, mm] = time.split(":").map(Number);
  const totalStartMin = hh * 60 + mm;
  // Build a Date in UTC purely for arithmetic — wall-clock semantics preserved
  const [y, mo, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0));
  dt.setUTCMinutes(totalStartMin + minutes);
  const yy = dt.getUTCFullYear();
  const mm2 = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  const hh2 = String(dt.getUTCHours()).padStart(2, "0");
  const mi2 = String(dt.getUTCMinutes()).padStart(2, "0");
  return `${yy}-${mm2}-${dd}T${hh2}:${mi2}`;
}

/**
 * Build a fresh "next future window" of Central datetime strings, used to
 * seed the order form so the create-button gating doesn't trip on stale
 * past-time defaults. Ready time is ceil-to-next-hour Central, ready_end is +1h,
 * delivery is +9h (mirrors the legacy 08:00→17:00 spread), delivery_end is
 * +10h. Pass a fixed `now` in tests for determinism.
 */
export interface SeededWindow {
  ready_time: string;
  ready_time_end: string;
  requested_delivery_time: string;
  requested_delivery_time_end: string;
}
export function seedFutureWindow(now: Date = new Date()): SeededWindow {
  const tz = PLANNING_TZ;
  const dateStr = formatInTimeZone(now, tz, "yyyy-MM-dd");
  const hour = parseInt(formatInTimeZone(now, tz, "HH"), 10);
  const min = parseInt(formatInTimeZone(now, tz, "mm"), 10);
  // Ceil the current wall-clock time up to the next multiple of the
  // configured window. With the default of 60 this reproduces the legacy
  // "next full hour" behavior; with e.g. 30 a user at 3:15 seeds to 3:30.
  const win = Math.max(1, DEFAULT_WINDOW_MINUTES);
  const totalMin = hour * 60 + min;
  let ceilTotal = totalMin % win === 0 ? totalMin : Math.ceil(totalMin / win) * win;
  let date = dateStr;
  if (ceilTotal >= 24 * 60) {
    const next = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    date = formatInTimeZone(next, tz, "yyyy-MM-dd");
    ceilTotal = 0;
  }
  const ceilHour = Math.floor(ceilTotal / 60);
  const ceilMin = ceilTotal % 60;
  const ready = `${date}T${String(ceilHour).padStart(2, "0")}:${String(ceilMin).padStart(2, "0")}`;
  return {
    ready_time: ready,
    ready_time_end: addMinutesLocal(ready, DEFAULT_WINDOW_MINUTES),
    requested_delivery_time: addMinutesLocal(ready, 4 * 60),
    requested_delivery_time_end: addMinutesLocal(ready, 4 * 60 + DEFAULT_WINDOW_MINUTES),
  };
}

/** Advanced field value for a leg's load state. `""` means "not set". */
export type LoadStatusValue = "" | "empty" | "loaded";

export interface LegData {
  id?: string;
  /** Where this leg's row lives. Always `"leg"` (a row in `public.legs`),
   *  which is the only storage shape for stops. */
  kind?: "leg";
  /** This leg's own `public.legs.driver_status`. Read-only in the form —
   *  it drives the per-leg edit rules in `src/lib/leg-edit-rules.ts` so one
   *  stop's progress never freezes a sibling stop. */
  driver_status?: string | null;
  ship_from_location_id: string;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string;
  ship_from_postal_zip_code: string;
  ship_from_country: string;
  ship_to_location_id: string;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string;
  ship_to_postal_zip_code: string;
  ship_to_country: string;
  ready_time: string;
  requested_delivery_time: string;
  ready_time_end: string;
  requested_delivery_time_end: string;
  /** Per-leg asset assignments. When `OrderFormData.use_same_assets_for_all_legs`
   *  is true, the form ignores these and propagates the order-level values to
   *  every leg at submit time. When false, each leg carries its own values. */
  assigned_tractor_id?: string;
  assigned_operator_id?: string;
  assigned_operator_2_id?: string;
  trailer_type_id?: string;
  /** Per-leg physical trailer id (human short code). When
   *  `OrderFormData.use_same_assets_for_all_legs` is true, the form ignores
   *  this and propagates the order-level `trailer_id` to every leg at submit
   *  time. When false, each leg carries its own trailer_id. */
  trailer_id?: string;
  trip_type?: string;
  /** Advanced ("Order API parity") fields — see `form/advancedFields.ts`.
   *  Shuttle carrier for this leg's origin / destination stop. */
  origin_shuttle_carrier_name?: string;
  origin_shuttle_carrier_dot?: string;
  destination_shuttle_carrier_name?: string;
  destination_shuttle_carrier_dot?: string;
  /** Equipment state for this leg. `""` = unset. */
  load_status?: LoadStatusValue;
  is_temperature_controlled?: boolean;
  /** Kept as a string so the numeric input can be cleared. */
  temperature_f?: string;
}

export const emptyLeg: LegData = {
  ship_from_location_id: "",
  ship_from_street_address: "",
  ship_from_city: "",
  ship_from_province_state: "",
  ship_from_postal_zip_code: "",
  ship_from_country: "",
  ship_to_location_id: "",
  ship_to_street_address: "",
  ship_to_city: "",
  ship_to_province_state: "",
  ship_to_postal_zip_code: "",
  ship_to_country: "",
  ready_time: `${todayDate()}T08:00`,
  requested_delivery_time: `${todayDate()}T17:00`,
  ready_time_end: `${todayDate()}T09:00`,
  requested_delivery_time_end: `${todayDate()}T18:00`,
  assigned_tractor_id: "",
  assigned_operator_id: "",
  assigned_operator_2_id: "",
  trailer_type_id: "",
  trailer_id: "",
  trip_type: "",
  origin_shuttle_carrier_name: "",
  origin_shuttle_carrier_dot: "",
  destination_shuttle_carrier_name: "",
  destination_shuttle_carrier_dot: "",
  load_status: "",
  is_temperature_controlled: false,
  temperature_f: "",
};

/** Factory variant that seeds future-safe times. Prefer this in form code so
 *  defaults are never in the past; `emptyLeg` is retained for tests. */
export function makeEmptyLeg(now: Date = new Date()): LegData {
  const w = seedFutureWindow(now);
  return { ...emptyLeg, ...w };
}

export interface OrderFormData {
  order_type: "single-leg" | "multi-leg";
  legs: LegData[];
  status: OrderStatus;
  ship_from_location_id: string;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string;
  ship_from_postal_zip_code: string;
  ship_from_country: string;
  ship_to_location_id: string;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string;
  ship_to_postal_zip_code: string;
  ship_to_country: string;
  ready_time: string;
  requested_delivery_time: string;
  ready_time_end: string;
  requested_delivery_time_end: string;
  billing_party_id: string;
  trailer_id: string;
  trailer_type_id: string;
  tender_id: string;
  assigned_tractor_id: string;
  assigned_operator_id: string;
  assigned_operator_2_id: string;
  is_recurring: boolean;
  recurrence_days: number[];
  recurrence_end_date: string;
  trip_type: string;
  cancellation_reason?: string;
  cancellation_notes?: string;
  /** Multi-leg orders only. When true (default), the order-level Tractor/Operator
   *  pickers apply to every leg. When false, per-leg pickers control each leg's
   *  assignment independently. Ignored for single-leg orders. */
  use_same_assets_for_all_legs?: boolean;
  /** Multi-leg orders only. When true (default), the order-level Trip Type
   *  applies to every leg. When false, each leg carries its own trip_type. */
  use_same_trip_type_for_all_legs?: boolean;
  /** UI-only: whether the "Advanced fields" inputs are expanded. Never persisted. */
  show_advanced_fields?: boolean;
  /** Advanced ("Order API parity") fields. Single-leg orders carry the stop
   *  shuttle carriers and equipment state here; multi-leg orders carry them
   *  per leg (see {@link LegData}). Shipment details are always order-level. */
  origin_shuttle_carrier_name?: string;
  origin_shuttle_carrier_dot?: string;
  destination_shuttle_carrier_name?: string;
  destination_shuttle_carrier_dot?: string;
  load_status?: LoadStatusValue;
  is_temperature_controlled?: boolean;
  temperature_f?: string;
  freight_weight_lbs?: string;
  commodity_code?: string;
  commodity_description?: string;
  /** Multi-leg only. Dispatcher-declared "Pending BOL": allows confirming
   *  before the BOL exists when at least one leg's trailer is physically
   *  empty. Persisted to `orders.pending_bol`; auto-cleared by the database
   *  once a BOL image is attached. */
  pending_bol?: boolean;
  /** Set when an admin acknowledged the Document Confirm Gating override
   *  dialog for this submit. Recorded as a `change_events` row after the
   *  write succeeds; never persisted on the order itself. */
  _docsOverride?: ConfirmDocsOverrideAck;
}

export const initialFormData: OrderFormData = {
  order_type: "single-leg",
  legs: [],
  status: "booked",
  ship_from_location_id: "",
  ship_from_street_address: "",
  ship_from_city: "",
  ship_from_province_state: "",
  ship_from_postal_zip_code: "",
  ship_from_country: "",
  ship_to_location_id: "",
  ship_to_street_address: "",
  ship_to_city: "",
  ship_to_province_state: "",
  ship_to_postal_zip_code: "",
  ship_to_country: "",
  ready_time: `${todayDate()}T08:00`,
  requested_delivery_time: `${todayDate()}T17:00`,
  ready_time_end: `${todayDate()}T09:00`,
  requested_delivery_time_end: `${todayDate()}T18:00`,
  billing_party_id: "",
  trailer_id: "",
  trailer_type_id: "",
  tender_id: "",
  assigned_tractor_id: "",
  assigned_operator_id: "",
  assigned_operator_2_id: "",
  trip_type: "",
  is_recurring: false,
  recurrence_days: [],
  recurrence_end_date: "",
  use_same_assets_for_all_legs: true,
  use_same_trip_type_for_all_legs: true,
  show_advanced_fields: false,
  origin_shuttle_carrier_name: "",
  origin_shuttle_carrier_dot: "",
  destination_shuttle_carrier_name: "",
  destination_shuttle_carrier_dot: "",
  load_status: "",
  is_temperature_controlled: false,
  temperature_f: "",
  freight_weight_lbs: "",
  commodity_code: "",
  commodity_description: "",
  pending_bol: false,
};

/** Factory that returns `initialFormData` with future-safe default times. */
export function makeInitialFormData(now: Date = new Date()): OrderFormData {
  return { ...initialFormData, ...seedFutureWindow(now) };
}
